import type {
  ApiError,
  DailyResponse,
  HealthDailyResponse,
  HealthTodayResponse,
  ManualState,
  MeResponse,
  SummaryResponse,
  WeeklyResponse,
} from "./types";
import type { QuestState } from "@/quest/types";

export class ApiClientError extends Error {
  status: number;
  code: string;
  reconnectUrl?: string;
  upstream?: { status: number };
  constructor(
    status: number,
    code: string,
    message: string,
    reconnectUrl?: string,
    upstream?: { status: number },
  ) {
    super(message);
    this.status = status;
    this.code = code;
    this.reconnectUrl = reconnectUrl;
    this.upstream = upstream;
  }
}

async function get<T>(path: string): Promise<T> {
  const res = await fetch(path, {
    credentials: "same-origin",
    headers: { accept: "application/json" },
  });
  if (!res.ok) throw await toApiError(res);
  return (await res.json()) as T;
}

async function post<T>(path: string, body?: unknown): Promise<T> {
  const res = await fetch(path, {
    method: "POST",
    credentials: "same-origin",
    headers: body
      ? { "content-type": "application/json", accept: "application/json" }
      : { accept: "application/json" },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) throw await toApiError(res);
  return (await res.json()) as T;
}

async function put<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(path, {
    method: "PUT",
    credentials: "same-origin",
    headers: { "content-type": "application/json", accept: "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw await toApiError(res);
  return (await res.json()) as T;
}

async function toApiError(res: Response): Promise<ApiClientError> {
  let body: ApiError | null = null;
  try {
    body = (await res.json()) as ApiError;
  } catch {
    /* ignore */
  }
  const code = body?.error?.code ?? "http";
  const message = body?.error?.message ?? res.statusText;
  return new ApiClientError(
    res.status,
    code,
    message,
    body?.error?.reconnectUrl,
    body?.error?.upstream,
  );
}

export const api = {
  me: () => get<MeResponse>("/api/me"),
  summary: () => get<SummaryResponse>("/api/azm/summary"),
  daily: (days: number) =>
    get<DailyResponse>(`/api/azm/daily?days=${days}`),
  weekly: (weeks: number) =>
    get<WeeklyResponse>(`/api/azm/weekly?weeks=${weeks}`),
  refresh: () => post<{ ok: true; fetchedAt: string }>("/api/azm/refresh"),
  healthToday: () => get<HealthTodayResponse>("/api/health/today"),
  healthDaily: (days: number) =>
    get<HealthDailyResponse>(`/api/health/daily?days=${days}`),
  stravaDaily: (days: number) =>
    get<HealthDailyResponse>(`/api/strava/daily?days=${days}`),
  manualState: () =>
    get<{ state: ManualState; updatedAt: string }>("/api/manual/state"),
  saveManualState: (state: ManualState) =>
    put<{ state: ManualState; updatedAt: string }>("/api/manual/state", {
      state,
    }),
  questState: () =>
    get<{ state: QuestState | null; updatedAt: string | null }>(
      "/api/quest/state",
    ),
  saveQuestState: (state: QuestState, expectedUpdatedAt?: string | null) =>
    put<{ state: QuestState; updatedAt: string }>("/api/quest/state", {
      state,
      expectedUpdatedAt: expectedUpdatedAt ?? null,
    }),
  logout: () => post<{ ok: true }>("/api/auth/logout"),
  debugRaw: (days: number) =>
    get<{
      range: { days: number };
      timezone: string;
      startDate: string;
      endDate: string;
      fetchedAt: string;
      normalized: { date: string; minutes: number }[];
      raw: unknown;
    }>(`/api/debug/raw?range=${days}`),
};
